// Client-side encryption utilities using Web Crypto API
// For securing sensitive financial data in IndexedDB

class EncryptionService {
  constructor() {
    this.algorithm = 'AES-GCM';
    this.keyLength = 256;
    this.ivLength = 12; // 96 bits for GCM
  }

  // Generate a new encryption key
  async generateKey() {
    try {
      const key = await crypto.subtle.generateKey(
        {
          name: this.algorithm,
          length: this.keyLength
        },
        true, // extractable
        ['encrypt', 'decrypt']
      );
      return key;
    } catch (error) {
      console.error('Failed to generate encryption key:', error);
      throw new Error('Encryption key generation failed');
    }
  }

  // Export key to store securely
  async exportKey(key) {
    try {
      const exported = await crypto.subtle.exportKey('jwk', key);
      return exported;
    } catch (error) {
      console.error('Failed to export key:', error);
      throw new Error('Key export failed');
    }
  }

  // Import key from storage
  async importKey(keyData) {
    try {
      const key = await crypto.subtle.importKey(
        'jwk',
        keyData,
        {
          name: this.algorithm,
          length: this.keyLength
        },
        true,
        ['encrypt', 'decrypt']
      );
      return key;
    } catch (error) {
      console.error('Failed to import key:', error);
      throw new Error('Key import failed');
    }
  }

  // Encrypt data
  async encrypt(data, key) {
    try {
      // Convert data to string if it's an object
      const plaintext = typeof data === 'string' ? data : JSON.stringify(data);
      const encoder = new TextEncoder();
      const encodedData = encoder.encode(plaintext);

      // Generate random IV
      const iv = crypto.getRandomValues(new Uint8Array(this.ivLength));

      // Encrypt the data
      const encrypted = await crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv: iv
        },
        key,
        encodedData
      );

      // Combine IV and encrypted data
      const result = new Uint8Array(iv.length + encrypted.byteLength);
      result.set(iv);
      result.set(new Uint8Array(encrypted), iv.length);

      // Convert to base64 for storage
      return btoa(String.fromCharCode(...result));
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Data encryption failed');
    }
  }

  // Decrypt data
  async decrypt(encryptedData, key) {
    try {
      // Convert from base64
      const combined = new Uint8Array(
        atob(encryptedData).split('').map(char => char.charCodeAt(0))
      );

      // Extract IV and encrypted data
      const iv = combined.slice(0, this.ivLength);
      const encrypted = combined.slice(this.ivLength);

      // Decrypt the data
      const decrypted = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: iv
        },
        key,
        encrypted
      );

      // Convert back to string
      const decoder = new TextDecoder();
      const plaintext = decoder.decode(decrypted);

      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(plaintext);
      } catch {
        return plaintext;
      }
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Data decryption failed');
    }
  }

  // Generate a key derivation from password (for user-based encryption)
  async deriveKeyFromPassword(password, salt) {
    try {
      const encoder = new TextEncoder();
      const passwordBuffer = encoder.encode(password);

      // Import password as key material
      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        passwordBuffer,
        'PBKDF2',
        false,
        ['deriveKey']
      );

      // Derive key using PBKDF2
      const key = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: salt,
          iterations: 100000, // High iteration count for security
          hash: 'SHA-256'
        },
        keyMaterial,
        {
          name: this.algorithm,
          length: this.keyLength
        },
        true,
        ['encrypt', 'decrypt']
      );

      return key;
    } catch (error) {
      console.error('Key derivation failed:', error);
      throw new Error('Password-based key derivation failed');
    }
  }

  // Generate salt for password-based encryption
  generateSalt() {
    return crypto.getRandomValues(new Uint8Array(16));
  }

  // Hash sensitive data (one-way)
  async hashData(data) {
    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(typeof data === 'string' ? data : JSON.stringify(data));
      
      const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
      const hashArray = new Uint8Array(hashBuffer);
      
      return btoa(String.fromCharCode(...hashArray));
    } catch (error) {
      console.error('Hashing failed:', error);
      throw new Error('Data hashing failed');
    }
  }
}

// Singleton instance
const encryptionService = new EncryptionService();

// Key management for the application
class KeyManager {
  constructor() {
    this.keyStorageKey = '70lives-encryption-key';
    this.saltStorageKey = '70lives-encryption-salt';
    this.currentKey = null;
  }

  // Initialize encryption for the app
  async initialize(userPassword = null) {
    try {
      let key;
      
      if (userPassword) {
        // Use password-based encryption
        let salt = this.getSalt();
        if (!salt) {
          salt = encryptionService.generateSalt();
          this.storeSalt(salt);
        }
        
        key = await encryptionService.deriveKeyFromPassword(userPassword, salt);
      } else {
        // Use stored key or generate new one
        const storedKey = this.getStoredKey();
        if (storedKey) {
          key = await encryptionService.importKey(storedKey);
        } else {
          key = await encryptionService.generateKey();
          const exportedKey = await encryptionService.exportKey(key);
          this.storeKey(exportedKey);
        }
      }
      
      this.currentKey = key;
      return true;
    } catch (error) {
      console.error('Key manager initialization failed:', error);
      return false;
    }
  }

  // Get current encryption key
  getCurrentKey() {
    return this.currentKey;
  }

  // Store key securely (in practice, this might use more secure storage)
  storeKey(keyData) {
    try {
      // In a real app, consider using more secure storage
      localStorage.setItem(this.keyStorageKey, JSON.stringify(keyData));
    } catch (error) {
      console.error('Failed to store encryption key:', error);
    }
  }

  // Retrieve stored key
  getStoredKey() {
    try {
      const stored = localStorage.getItem(this.keyStorageKey);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to retrieve encryption key:', error);
      return null;
    }
  }

  // Store salt for password-based encryption
  storeSalt(salt) {
    try {
      const saltBase64 = btoa(String.fromCharCode(...salt));
      localStorage.setItem(this.saltStorageKey, saltBase64);
    } catch (error) {
      console.error('Failed to store salt:', error);
    }
  }

  // Get stored salt
  getSalt() {
    try {
      const stored = localStorage.getItem(this.saltStorageKey);
      if (stored) {
        return new Uint8Array(atob(stored).split('').map(char => char.charCodeAt(0)));
      }
      return null;
    } catch (error) {
      console.error('Failed to retrieve salt:', error);
      return null;
    }
  }

  // Clear all encryption data (for logout/reset)
  clearEncryptionData() {
    localStorage.removeItem(this.keyStorageKey);
    localStorage.removeItem(this.saltStorageKey);
    this.currentKey = null;
  }
}

// Singleton key manager
const keyManager = new KeyManager();

// High-level encryption utilities for the app
export const AppEncryption = {
  // Initialize encryption system
  async initialize(userPassword = null) {
    return await keyManager.initialize(userPassword);
  },

  // Encrypt sensitive transaction data
  async encryptTransaction(transaction) {
    const key = keyManager.getCurrentKey();
    if (!key) {
      throw new Error('Encryption not initialized');
    }

    // Only encrypt sensitive fields
    const sensitiveFields = ['amount', 'description', 'vatAmount'];
    const encrypted = { ...transaction };

    for (const field of sensitiveFields) {
      if (transaction[field] !== undefined && transaction[field] !== null) {
        encrypted[field] = await encryptionService.encrypt(transaction[field], key);
        encrypted[`${field}_encrypted`] = true;
      }
    }

    return encrypted;
  },

  // Decrypt transaction data
  async decryptTransaction(encryptedTransaction) {
    const key = keyManager.getCurrentKey();
    if (!key) {
      throw new Error('Encryption not initialized');
    }

    const decrypted = { ...encryptedTransaction };
    const sensitiveFields = ['amount', 'description', 'vatAmount'];

    for (const field of sensitiveFields) {
      if (encryptedTransaction[`${field}_encrypted`]) {
        try {
          decrypted[field] = await encryptionService.decrypt(encryptedTransaction[field], key);
          delete decrypted[`${field}_encrypted`];
        } catch (error) {
          console.error(`Failed to decrypt field ${field}:`, error);
          // Keep encrypted data if decryption fails
        }
      }
    }

    return decrypted;
  },

  // Encrypt service prices
  async encryptServicePrices(servicePrices) {
    const key = keyManager.getCurrentKey();
    if (!key) {
      throw new Error('Encryption not initialized');
    }

    return await encryptionService.encrypt(servicePrices, key);
  },

  // Decrypt service prices
  async decryptServicePrices(encryptedServicePrices) {
    const key = keyManager.getCurrentKey();
    if (!key) {
      throw new Error('Encryption not initialized');
    }

    return await encryptionService.decrypt(encryptedServicePrices, key);
  },

  // Hash sensitive search terms
  async hashSearchTerm(term) {
    return await encryptionService.hashData(term);
  },

  // Check if encryption is available
  isAvailable() {
    return 'crypto' in window && 'subtle' in window.crypto;
  },

  // Clear encryption data
  clearData() {
    keyManager.clearEncryptionData();
  }
};

// Initialize encryption on app start
export const initializeEncryption = async () => {
  if (!AppEncryption.isAvailable()) {
    console.warn('Web Crypto API not available - encryption disabled');
    return false;
  }

  try {
    const initialized = await AppEncryption.initialize();
    if (initialized) {
      console.log('Client-side encryption initialized successfully');
    } else {
      console.warn('Failed to initialize encryption');
    }
    return initialized;
  } catch (error) {
    console.error('Encryption initialization error:', error);
    return false;
  }
};

export { encryptionService, keyManager };
export default AppEncryption;